import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';

/**
 * The daily inputs, on the web.
 *
 * Everything here already existed as an endpoint and was only reachable from
 * the phone. That is a worse state than not having the feature: the data goes
 * in on one device and is invisible on the other, so the numbers on the two
 * screens disagree and neither says why.
 *
 * The scales are one to five with words on them rather than numbers alone. "3"
 * means nothing six months later; "average" still does.
 */

interface Inputs {
  date: string;
  waterMl: number | null;
  sleepMinutes: number | null;
  sleepQuality: number | null;
  mood: number | null;
  energy: number | null;
  soreness: number | null;
  stress: number | null;
  steps: number | null;
  notes: string | null;
}

interface BpCategory { label: string; urgent: boolean; detail?: string }
interface Vitals {
  latest: { takenAt: string; systolic: number | null; diastolic: number | null; restingHr: number | null } | null;
  category: BpCategory | null;
  average?: { systolic: number; diastolic: number; readings: number } | null;
}

interface DailyResponse { inputs: Inputs; waterTargetMl: number | null; vitals: Vitals }

const SCALES: Record<string, [string, string, string, string, string]> = {
  sleepQuality: ['Awful', 'Poor', 'Average', 'Good', 'Excellent'],
  mood: ['Awful', 'Low', 'Average', 'Good', 'Great'],
  energy: ['Flat', 'Low', 'Average', 'Good', 'High'],
  soreness: ['None', 'Slight', 'Noticeable', 'Sore', 'Very sore'],
  stress: ['None', 'Slight', 'Average', 'High', 'Very high'],
};

const LABEL: Record<string, string> = {
  sleepQuality: 'Sleep quality', mood: 'Mood', energy: 'Energy',
  soreness: 'Soreness', stress: 'Stress',
};

function Scale({ field, value, onPick }: {
  field: keyof typeof SCALES; value: number | null; onPick: (v: number | null) => void;
}): JSX.Element {
  const words = SCALES[field]!;
  return (
    <div className="field">
      <span>{LABEL[field]}</span>
      <div className="row wrap">
        {words.map((w, i) => (
          <button key={w} type="button"
                  className={value === i + 1 ? 'on' : ''}
                  // Tapping the current value clears it. Without that there is
                  // no way to undo a mis-tap, and a wrong 5 sitting in the
                  // record forever is worse than a blank.
                  onClick={() => onPick(value === i + 1 ? null : i + 1)}>
            {w}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Daily(): JSX.Element {
  const [data, setData] = useState<DailyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [bp, setBp] = useState({ systolic: '', diastolic: '', restingHr: '' });
  const [bpMessage, setBpMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api<DailyResponse>('/daily');
      setData(d);
      setNotes(d.inputs.notes ?? '');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load today.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const patch = async (body: Record<string, unknown>): Promise<void> => {
    setSaving(true);
    try {
      const r = await api<{ inputs: Inputs }>('/daily', { method: 'PATCH', body: JSON.stringify(body) });
      setData((d) => (d ? { ...d, inputs: r.inputs } : d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save.');
    } finally {
      setSaving(false);
    }
  };

  const water = async (ml: number): Promise<void> => {
    try {
      const r = await api<{ inputs: Inputs; waterTargetMl: number | null }>(
        '/daily/water', { method: 'POST', body: JSON.stringify({ ml }) });
      setData((d) => (d ? { ...d, inputs: r.inputs, waterTargetMl: r.waterTargetMl } : d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not save.');
    }
  };

  const submitBp = async (): Promise<void> => {
    const s = Number(bp.systolic), dia = Number(bp.diastolic);
    if (!s || !dia) { setBpMessage('Both numbers are needed.'); return; }
    try {
      const r = await api<{ vitals: Vitals; urgent: string | null }>('/daily/vitals', {
        method: 'POST',
        body: JSON.stringify({
          systolic: s, diastolic: dia,
          restingHr: bp.restingHr ? Number(bp.restingHr) : null,
        }),
      });
      setData((d) => (d ? { ...d, vitals: r.vitals } : d));
      setBp({ systolic: '', diastolic: '', restingHr: '' });
      setBpMessage(r.urgent ?? 'Saved.');
    } catch (e) {
      setBpMessage(e instanceof Error ? e.message : 'That did not save.');
    }
  };

  if (error && !data) return <div className="card"><p className="bad">{error}</p></div>;
  if (!data) return <div className="card"><p className="muted small">Loading.</p></div>;

  const i = data.inputs;
  const target = data.waterTargetMl;
  const drunk = i.waterMl ?? 0;

  return (
    <>
      <div className="card">
        <h2>Water</h2>
        <p className="big">
          {(drunk / 1000).toFixed(1)} L
          {target ? <span className="muted small"> of {(target / 1000).toFixed(1)} L</span> : null}
        </p>
        <div className="row wrap">
          {[250, 500, 750].map((ml) => (
            <button key={ml} type="button" className="ghost" onClick={() => void water(ml)}>
              +{ml} ml
            </button>
          ))}
          {drunk > 0 && (
            <button type="button" className="ghost" onClick={() => void patch({ waterMl: 0 })}>
              Reset
            </button>
          )}
        </div>
        {target == null && (
          <p className="muted small">
            No target yet. It is worked out from your weight, so it appears once a scan or a weight
            is on file.
          </p>
        )}
      </div>

      <div className="card">
        <h2>Sleep</h2>
        <div className="row wrap">
          <label className="field">
            <span>Hours</span>
            <input type="number" step="0.25" min="0" max="24"
                   value={i.sleepMinutes != null ? (i.sleepMinutes / 60).toFixed(2).replace(/\.?0+$/, '') : ''}
                   onChange={(e) => {
                     const h = Number(e.target.value);
                     void patch({ sleepMinutes: e.target.value === '' ? null : Math.round(h * 60) });
                   }} />
          </label>
        </div>
        <Scale field="sleepQuality" value={i.sleepQuality} onPick={(v) => void patch({ sleepQuality: v })} />
      </div>

      <div className="card">
        <h2>How the day feels</h2>
        <Scale field="mood" value={i.mood} onPick={(v) => void patch({ mood: v })} />
        <Scale field="energy" value={i.energy} onPick={(v) => void patch({ energy: v })} />
        <Scale field="soreness" value={i.soreness} onPick={(v) => void patch({ soreness: v })} />
        <Scale field="stress" value={i.stress} onPick={(v) => void patch({ stress: v })} />
        <p className="muted small">
          These are what the correlations on the Trends page are built from. They only become
          useful after a couple of weeks, and a day skipped is better than a day guessed.
        </p>
      </div>

      <div className="card">
        <h2>Steps</h2>
        <input type="number" min="0" max="200000" value={i.steps ?? ''}
               onChange={(e) => void patch({ steps: e.target.value === '' ? null : Number(e.target.value) })} />
        <p className="muted small">
          If you sync a watch through the Android app, this fills in on its own — and a number you
          type here always wins over one the watch reported.
        </p>
      </div>

      <div className="card">
        <h2>Blood pressure</h2>
        {data.vitals.latest ? (
          <p>
            <strong>
              {data.vitals.latest.systolic}/{data.vitals.latest.diastolic}
            </strong>
            {data.vitals.category && (
              <span className={data.vitals.category.urgent ? 'bad' : 'muted'}>
                {' '}— {data.vitals.category.label}
              </span>
            )}
          </p>
        ) : (
          <p className="muted small">Nothing recorded yet.</p>
        )}

        <div className="row wrap">
          <label className="field">
            <span>Systolic</span>
            <input type="number" value={bp.systolic}
                   onChange={(e) => setBp((b) => ({ ...b, systolic: e.target.value }))} />
          </label>
          <label className="field">
            <span>Diastolic</span>
            <input type="number" value={bp.diastolic}
                   onChange={(e) => setBp((b) => ({ ...b, diastolic: e.target.value }))} />
          </label>
          <label className="field">
            <span>Resting HR</span>
            <input type="number" value={bp.restingHr}
                   onChange={(e) => setBp((b) => ({ ...b, restingHr: e.target.value }))} />
          </label>
        </div>
        <button type="button" onClick={() => void submitBp()}>Record</button>
        {bpMessage && (
          <p className={bpMessage.includes('crisis') ? 'bad' : 'muted small'}>{bpMessage}</p>
        )}
        <p className="muted small">
          Take three readings a minute apart and record the second and third. The first is almost
          always the highest and it is the one that makes people think they have a problem they
          do not.
        </p>
      </div>

      <div className="card">
        <h2>Notes</h2>
        <textarea rows={3} value={notes} maxLength={2000}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={() => { if (notes !== (i.notes ?? '')) void patch({ notes: notes || null }); }} />
        <p className="muted small">{saving ? 'Saving…' : 'Saved when you click away.'}</p>
      </div>

      {error && <p className="bad">{error}</p>}
    </>
  );
}
